import { Stack, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EmailComposer from './components/emailComposer';
import ViewerHeader from './components/ViewerHeader';
import { useGetProductLevel } from 'src/@core/utils/getProductLevel';
import { MessagingServiceDto } from 'src/models/messaging';
import dynamic from 'next/dynamic';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import { AttachmentDto, TrackingAttachmentDto } from 'src/models/attachment';
import { SalesOrderStatusHistoryDto } from 'src/models/salesorder';
import { StatusPanelItems } from 'src/@vigo/vigo-orderstatuspanel';
import { getDocumentSelectionKey } from './helpers';
import { LoadedDocumentFile, useDocumentViewerFiles } from './helpers/useDocumentViewerFiles';

const ViewerDocument = dynamic(() => import('./components/ViewerDocument'), {
  ssr: false
});

interface DocumentViewerProps {
  open: boolean;
  handleClose: () => void;
  documents?: (AttachmentDto | TrackingAttachmentDto)[];
  useOverride: boolean;
  orderId: string;
  consignmentNumber: string;
  messagingService?: MessagingServiceDto;
  statuses: SalesOrderStatusHistoryDto[];
  orderItems?: StatusPanelItems[];

  /**
   * Pre-fetched documents to display. Required in override (public tracking) mode, where the
   * authenticated attachments query is not available to guests — pass the tracking attachments
   * (from getTrackingAttachments, which carry the per-document `hash`) here instead.
   */
  documentsOverride?: (AttachmentDto | TrackingAttachmentDto)[];
  refetchCallback?: () => void;
}

const DocumentViewer = ({
  open,
  handleClose,
  documents: documentsProp,
  useOverride,
  orderId,
  consignmentNumber,
  messagingService,
  statuses,
  orderItems,
  documentsOverride,
  refetchCallback
}: DocumentViewerProps) => {
  // State
  const [selectedDocuments, setSelectedDocuments] = useState<Array<string>>([]);
  const [emailOpen, setEmailOpen] = useState(false);

  // Hooks
  const theme = useTheme();
  const prod = useGetProductLevel();
  const { vigoToast } = useVigoToast({});

  const handlePartialLoadFailure = useCallback(() => {
    vigoToast.error('Some documents failed to load.');
  }, [vigoToast]);

  // In override (public tracking) mode use the passed-in tracking attachments (they carry the
  // per-document `hash` guests need); otherwise use the attachments supplied by the parent.
  const documents = useMemo<(AttachmentDto | TrackingAttachmentDto)[]>(
    () => (useOverride ? documentsOverride ?? documentsProp ?? [] : documentsProp ?? []),
    [useOverride, documentsOverride, documentsProp]
  );

  const { files, documentsForProduct, isLoading } = useDocumentViewerFiles({
    documents,
    useOverride,
    product: prod,
    onPartialFailure: handlePartialLoadFailure,
    enabled: open
  });

  /**
   * Open the email composer.
   */
  const handleEmailClick = useCallback(() => {
    setEmailOpen(true);
  }, []);

  /**
   * Get the selected files
   */
  const getSelectedFiles = (
    files: LoadedDocumentFile[],
    selectedDocuments: string[]
  ) => {
    return files.reduce((acc, file) => {
      if (selectedDocuments.includes(getDocumentSelectionKey(file.document, file.file.name))) {
        acc.push(file.file);
      }

      return acc;
    }, [] as File[]);
  };

  /**
   * Handle downloading of selected documents.
   */
  const handleDownloadClick = useCallback(() => {
    const filesToDownload = getSelectedFiles(files, selectedDocuments);

    if (filesToDownload.length === 0) {
      vigoToast.error('No files selected for download');

      return;
    }

    for (let i = 0; i < filesToDownload.length; i++) {
      const blob = new Blob([filesToDownload[i]], { type: filesToDownload[i].type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filesToDownload[i].name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }, [files, selectedDocuments, vigoToast]);

  useEffect(() => {
    setSelectedDocuments([]);
  }, [documentsForProduct]);

  return (
    <Stack
      direction="column"
      sx={{
        transformOrigin: 'top right',
        transform: open ? 'scale(1)' : 'scale(0)',
        transition: 'transform 0.3s ease',
        zIndex: 9999,
        backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e1e',
        padding: '15px 20px',
        display: 'flex',
        color: theme => theme.palette.vigoColors.primaryLight,
        fontSize: '1.143rem',
        fontWeight: 'bold',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'auto'
      }}>
      <ViewerHeader
        handleClose={handleClose}
        count={documents?.length ?? 0}
        handleEmailClick={handleEmailClick}
        handleDownloadClick={handleDownloadClick}
        {...{ useOverride }}
      />

      <Stack direction="column" gap={8} sx={{ height: '100%', overflowY: 'auto', paddingBottom: '20px' }}>
        {files.length > 0 ? (
          files.map(({ file, document }, index) => (
            <ViewerDocument
              key={getDocumentSelectionKey(document, `${file.name}-${index}`)}
              {...{
                file,
                selectedDocuments,
                setSelectedDocuments,
                document,
                statuses,
                orderItems,
                refetchCallback
              }}
            />
          ))
        ) : isLoading ? (
          <div>Loading documents...</div>
        ) : (
          <div>No documents available</div>
        )}
      </Stack>

      {!useOverride && emailOpen && (
        <EmailComposer
          open={emailOpen}
          handleClose={() => {
            setEmailOpen(false);
          }}
          {...{
            messagingService,
            orderId,
            consignmentNumber
          }}
        />
      )}
    </Stack>
  );
};

export default DocumentViewer;
